import { cx } from "@emotion/css";
import { useFormik } from "formik";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { FlashcardsTipTapEditor } from "src/components/TipTapEditor/FlashcardsTipTapEditor";

interface FlashcardEditorProps {
	initialFront?: string;
	initialBack?: string;
	onSave: (data: { front: string; back: string }) => void | Promise<void>;
	onCancel: () => void;
	isSaving?: boolean;
	keySuffix?: string | number;
	testIdPrefix?: string;
	bare?: boolean;
	hideFooter?: boolean;
	onValidChange?: (isValid: boolean) => void;
	/** Tryb edycji istniejącej fiszki: obramowana karta z nagłówkiem i podpowiedzią skrótu. */
	isEditing?: boolean;
}

export interface FlashcardEditorHandle {
	save: () => void;
	/** Aktualna treść, jeśli obie strony są wypełnione — inaczej null. */
	getValidValues: () => FlashcardFormValues | null;
}

interface FlashcardFormValues {
	front: string;
	back: string;
}

function isEmptyHtml(html: string) {
	if (!html) return true;
	if (/<img\s/i.test(html)) return false;
	const stripped = html.replace(/<[^>]*>/g, "").trim();
	return stripped.length === 0;
}

export const FlashcardEditor = forwardRef<
	FlashcardEditorHandle,
	FlashcardEditorProps
>(function FlashcardEditor(
	{
		initialFront = "",
		initialBack = "",
		onSave,
		onCancel,
		isSaving,
		keySuffix = "new",
		testIdPrefix = "flashcard-edit",
		bare,
		hideFooter,
		onValidChange,
		isEditing,
	},
	ref,
) {
	const { t } = useTranslation();

	const formik = useFormik<FlashcardFormValues>({
		initialValues: { front: initialFront, back: initialBack },
		validate: (values) => {
			const errors: Partial<FlashcardFormValues> = {};
			if (isEmptyHtml(values.front)) {
				errors.front = t("errors.emptyFlashcards");
			}
			if (isEmptyHtml(values.back)) {
				errors.back = t("errors.emptyFlashcards");
			}
			return errors;
		},
		onSubmit: (values) => onSave(values),
	});

	const isValid =
		!isEmptyHtml(formik.values.front) && !isEmptyHtml(formik.values.back);

	useEffect(() => {
		onValidChange?.(isValid);
	}, [isValid, onValidChange]);

	useImperativeHandle(
		ref,
		() => ({
			save: () => formik.handleSubmit(),
			getValidValues: () => (isValid ? formik.values : null),
		}),
		[formik.handleSubmit, formik.values, isValid],
	);

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
			event.preventDefault();
			formik.handleSubmit();
		}
	};

	const body = (
		<div
			className="flashcard--deck--add-new-flashcard--container w-100"
			onKeyDown={handleKeyDown}
		>
			{isEditing && (
				<div className="flex items-center justify-between gap-8 mb-12">
					<span className="size-14 text-primary">
						{t("decks.editor.editing", "Edytujesz fiszkę")}
					</span>
					<span className="size-12-light text-grey">
						{t("decks.editor.saveShortcut", "Ctrl + Enter zapisuje")}
					</span>
				</div>
			)}
			<div className="flashcard--deck--add-new-flashcard">
				<FlashcardsTipTapEditor
					key={`front-${keySuffix}`}
					className="flashcard--tiptap-editor"
					initialHtml={initialFront}
					onUpdate={(html: string) =>
						formik.setFieldValue("front", html, false)
					}
					placeholder={t(
						"decks.frontPlaceholder",
						"Pytanie lub pojęcie, np. „Objawy choroby Scheuermanna”",
					)}
					label={isEditing ? undefined : t("decks.front")}
					data-test-id={`${testIdPrefix}-front-${keySuffix}`}
				/>
				<FlashcardsTipTapEditor
					key={`back-${keySuffix}`}
					className="flashcard--tiptap-editor"
					initialHtml={initialBack}
					onUpdate={(html: string) => formik.setFieldValue("back", html, false)}
					placeholder={t(
						"decks.backPlaceholder",
						"Odpowiedź lub definicja. Źródło możesz dodać jako cytat.",
					)}
					label={isEditing ? undefined : t("decks.back")}
					data-test-id={`${testIdPrefix}-back-${keySuffix}`}
				/>
			</div>
			{!hideFooter && (
				<div className="flex items-center justify-end mt-12 gap-8">
					<TextPrimaryButton
						onClick={onCancel}
						disabled={isSaving}
						data-test-id={`${testIdPrefix}-cancel-${keySuffix}`}
					>
						{t("commands.cancel")}
					</TextPrimaryButton>
					<PrimaryButton
						onClick={() => formik.handleSubmit()}
						disabled={isSaving || !isValid}
						data-test-id={`${testIdPrefix}-save-${keySuffix}`}
					>
						{t("decks.editor.save", "Zapisz fiszkę")}
					</PrimaryButton>
				</div>
			)}
		</div>
	);

	if (bare) {
		return body;
	}

	return (
		<div
			className={cx(
				"flashcards-list-element flashcards-list-element--card",
				isEditing && "flashcards-list-element--editing",
			)}
		>
			{body}
		</div>
	);
});
